import { useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  Typography,
  Chip,
  Button,
  Grid,
  CircularProgress,
  alpha,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import WarningIcon from '@mui/icons-material/Warning';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptIcon from '@mui/icons-material/Receipt';
import StoreIcon from '@mui/icons-material/Store';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { Delivery, DeliveryStatus } from '../../types';

const fetchDelivery = async (id: string): Promise<Delivery> => {
  const response = await axiosInstance.get(`/deliveries/${id}`);
  return response.data;
};

const updateDeliveryStatus = async ({
  id,
  status,
  failureReason,
}: {
  id: string;
  status: DeliveryStatus;
  failureReason?: string;
}) => {
  const response = await axiosInstance.patch(`/deliveries/${id}/status`, { status, failureReason });
  return response.data;
};

export const DeliveryShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();

  const [failedDialogOpen, setFailedDialogOpen] = useState(false);
  const [failureReason, setFailureReason] = useState('');

  const { data: delivery, isLoading } = useQuery({
    queryKey: ['delivery', id],
    queryFn: () => fetchDelivery(id!),
    enabled: !!id,
  });

  const statusMutation = useMutation({
    mutationFn: updateDeliveryStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery', id] });
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      showMessage('Delivery status updated', 'success');
    },
    onError: (error: any) => showMessage(error.response?.data?.message || 'Failed to update delivery status', 'error'),
  });

  const getStatusColor = (status: DeliveryStatus): string => {
    switch (status) {
      case 'pending':
        return palette.warning;
      case 'assigned':
        return palette.info;
      case 'picked_up':
        return palette.accent;
      case 'out_for_delivery':
        return '#9C27B0';
      case 'delivered':
        return palette.success;
      case 'failed':
        return palette.error;
      case 'cancelled':
        return palette.textMuted;
      default:
        return palette.textMuted;
    }
  };

  const getStatusIcon = (status: DeliveryStatus) => {
    switch (status) {
      case 'pending':
        return <HourglassEmptyIcon />;
      case 'assigned':
        return <AssignmentIndIcon />;
      case 'picked_up':
        return <InventoryIcon />;
      case 'out_for_delivery':
        return <DirectionsCarIcon />;
      case 'delivered':
        return <CheckCircleIcon />;
      case 'failed':
        return <WarningIcon />;
      case 'cancelled':
        return <CancelIcon />;
      default:
        return null;
    }
  };

  const formatStatusLabel = (status: DeliveryStatus): string => {
    return status.replace(/_/g, ' ').toUpperCase();
  };

  const formatDateTime = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('fr-DZ', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleStatusUpdate = (newStatus: DeliveryStatus) => {
    if (newStatus === 'failed') {
      setFailedDialogOpen(true);
    } else {
      statusMutation.mutate({ id: id!, status: newStatus });
    }
  };

  const handleFailedConfirm = () => {
    statusMutation.mutate({ id: id!, status: 'failed', failureReason });
    setFailedDialogOpen(false);
    setFailureReason('');
  };

  const colors =
    mode === 'dark'
      ? {
          bg: palette.background,
          surface: palette.surface,
          surfaceElevated: palette.surfaceElevated,
          border: palette.border,
          borderLight: palette.borderLight,
          textPrimary: palette.textPrimary,
          textSecondary: palette.textSecondary,
          textMuted: palette.textMuted,
        }
      : {
          bg: palette.light.background,
          surface: palette.light.surface,
          surfaceElevated: palette.light.surfaceElevated,
          border: palette.light.border,
          borderLight: palette.light.borderLight,
          textPrimary: palette.light.textPrimary,
          textSecondary: palette.light.textSecondary,
          textMuted: palette.light.textMuted,
        };

  // Timeline events
  const timelineEvents = delivery
    ? [
        { label: 'Created', time: delivery.createdAt, status: 'pending' as DeliveryStatus },
        delivery.assignedAt && {
          label: 'Assigned',
          time: delivery.assignedAt,
          status: 'assigned' as DeliveryStatus,
        },
        delivery.pickedUpAt && {
          label: 'Picked Up',
          time: delivery.pickedUpAt,
          status: 'picked_up' as DeliveryStatus,
        },
        delivery.outForDeliveryAt && {
          label: 'Out for Delivery',
          time: delivery.outForDeliveryAt,
          status: 'out_for_delivery' as DeliveryStatus,
        },
        delivery.deliveredAt && {
          label: 'Delivered',
          time: delivery.deliveredAt,
          status: 'delivered' as DeliveryStatus,
        },
        delivery.failedAt && {
          label: 'Failed',
          time: delivery.failedAt,
          status: 'failed' as DeliveryStatus,
        },
        delivery.cancelledAt && {
          label: 'Cancelled',
          time: delivery.cancelledAt,
          status: 'cancelled' as DeliveryStatus,
        },
      ].filter(Boolean)
    : [];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: palette.primary }} />
      </Box>
    );
  }

  if (!delivery) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" color="error">
          Delivery not found
        </Typography>
      </Box>
    );
  }

  const statusColor = getStatusColor(delivery.status);

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Header */}
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton
            onClick={() => navigate('/app/deliveries')}
            sx={{
              border: `1px solid ${colors.border}`,
              borderRadius: theme.radius.xs,
              color: colors.textPrimary,
              '&:hover': {
                borderColor: palette.primary,
                backgroundColor: alpha(palette.primary, 0.1),
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <LocalShippingIcon sx={{ color: palette.primary, fontSize: 20 }} />
              <Typography
                variant="overline"
                sx={{
                  color: palette.primary,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                DELIVERY DETAILS
              </Typography>
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                color: colors.textPrimary,
                letterSpacing: '-0.02em',
              }}
            >
              #{delivery.id.substring(0, 8).toUpperCase()}
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto' }}>
            <Chip
              icon={getStatusIcon(delivery.status) || undefined}
              label={formatStatusLabel(delivery.status)}
              sx={{
                backgroundColor: alpha(statusColor, 0.1),
                color: statusColor,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.bold,
                border: `2px solid ${statusColor}`,
                px: 2,
                py: 2.5,
                '& .MuiChip-icon': {
                  color: statusColor,
                  fontSize: 20,
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Main Info */}
        <Grid item xs={12} lg={8}>
          {/* Customer & Address Card */}
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 3,
              mb: 3,
              ...animations.slideUp(0.1),
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                letterSpacing: typography.letterSpacing.widest,
                fontWeight: typography.fontWeight.bold,
              }}
            >
              CUSTOMER INFORMATION
            </Typography>

            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      backgroundColor: palette.primary,
                    }}
                  >
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: typography.fontFamily.display,
                        fontWeight: typography.fontWeight.bold,
                        color: colors.textPrimary,
                      }}
                    >
                      {delivery.customerName || 'N/A'}
                    </Typography>
                    {delivery.customerPhone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <PhoneIcon sx={{ fontSize: 16, color: colors.textMuted }} />
                        <Typography
                          variant="body2"
                          sx={{ color: colors.textSecondary, fontFamily: typography.fontFamily.mono }}
                        >
                          {delivery.customerPhone}
                        </Typography>
                      </Box>
                    )}
                    {delivery.customerEmail && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <EmailIcon sx={{ fontSize: 16, color: colors.textMuted }} />
                        <Typography
                          variant="body2"
                          sx={{ color: colors.textSecondary, fontFamily: typography.fontFamily.mono }}
                        >
                          {delivery.customerEmail}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: theme.radius.sm,
                      backgroundColor: alpha(palette.error, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <LocationOnIcon sx={{ color: palette.error }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: '10px',
                      }}
                    >
                      DELIVERY ADDRESS
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: typography.fontFamily.display,
                        fontWeight: typography.fontWeight.semibold,
                        color: colors.textPrimary,
                      }}
                    >
                      {delivery.deliveryAddress?.street}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                      {delivery.deliveryAddress?.city}, {delivery.deliveryAddress?.state}{' '}
                      {delivery.deliveryAddress?.postalCode}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.textMuted }}>
                      {delivery.deliveryAddress?.country}
                    </Typography>
                    {delivery.deliveryAddress?.notes && (
                      <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                        <Typography variant="caption">{delivery.deliveryAddress.notes}</Typography>
                      </Alert>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Card>

          {/* Pickup & Driver Card */}
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 3,
              mb: 3,
              ...animations.slideUp(0.2),
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    letterSpacing: typography.letterSpacing.widest,
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  PICKUP LOCATION
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: theme.radius.sm,
                      backgroundColor: alpha(palette.info, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <StoreIcon sx={{ color: palette.info }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: typography.fontFamily.display,
                        fontWeight: typography.fontWeight.semibold,
                        color: colors.textPrimary,
                      }}
                    >
                      {delivery.pickupLocation?.name || 'N/A'}
                    </Typography>
                    {delivery.pickupLocation?.address && (
                      <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                        {delivery.pickupLocation.address}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    letterSpacing: typography.letterSpacing.widest,
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  ASSIGNED DRIVER
                </Typography>
                {delivery.driver ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        backgroundColor: palette.success,
                      }}
                    >
                      {delivery.driver.firstName?.[0]}
                      {delivery.driver.lastName?.[0]}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{
                          fontFamily: typography.fontFamily.display,
                          fontWeight: typography.fontWeight.semibold,
                          color: colors.textPrimary,
                        }}
                      >
                        {delivery.driver.firstName} {delivery.driver.lastName}
                      </Typography>
                      {delivery.driver.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                          <Typography
                            variant="body2"
                            sx={{ color: colors.textSecondary, fontFamily: typography.fontFamily.mono }}
                          >
                            {delivery.driver.phone}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label="NO DRIVER ASSIGNED"
                      sx={{
                        backgroundColor: alpha(palette.warning, 0.1),
                        color: palette.warning,
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.bold,
                        border: `1px solid ${palette.warning}`,
                      }}
                    />
                    {delivery.status === 'pending' && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AssignmentIndIcon />}
                        onClick={() => navigate(`/app/deliveries/${id}/assign`)}
                        sx={{ ml: 2, textTransform: 'uppercase' }}
                      >
                        Assign Driver
                      </Button>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Card>

          {/* Notes Card */}
          {(delivery.notes || delivery.driverNotes || delivery.failureReason) && (
            <Card
              sx={{
                backgroundColor: colors.surface,
                border: `2px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                p: 3,
                mb: 3,
                ...animations.slideUp(0.3),
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                NOTES
              </Typography>

              {delivery.notes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    Delivery Notes:
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.textPrimary, mt: 0.5 }}>
                    {delivery.notes}
                  </Typography>
                </Box>
              )}

              {delivery.driverNotes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    Driver Notes:
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.textPrimary, mt: 0.5 }}>
                    {delivery.driverNotes}
                  </Typography>
                </Box>
              )}

              {delivery.failureReason && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Failure Reason:</strong> {delivery.failureReason}
                  </Typography>
                </Alert>
              )}
            </Card>
          )}

          {/* Reservation Link */}
          {delivery.reservation && (
            <Card
              sx={{
                backgroundColor: colors.surface,
                border: `2px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                p: 3,
                ...animations.slideUp(0.4),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ReceiptIcon sx={{ color: palette.primary }} />
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: '10px',
                      }}
                    >
                      LINKED RESERVATION
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.bold,
                        color: colors.textPrimary,
                      }}
                    >
                      #{delivery.reservationId.substring(0, 8).toUpperCase()}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/app/reservations/show/${delivery.reservationId}`)}
                  sx={{ textTransform: 'uppercase' }}
                >
                  View Reservation
                </Button>
              </Box>
            </Card>
          )}
        </Grid>

        {/* Right Column - Timeline & Actions */}
        <Grid item xs={12} lg={4}>
          {/* Actions Card */}
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 3,
              mb: 3,
              ...animations.slideUp(0.1),
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                letterSpacing: typography.letterSpacing.widest,
                fontWeight: typography.fontWeight.bold,
              }}
            >
              ACTIONS
            </Typography>

            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {delivery.status === 'pending' && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AssignmentIndIcon />}
                  onClick={() => navigate(`/app/deliveries/${id}/assign`)}
                  sx={{
                    backgroundColor: palette.info,
                    textTransform: 'uppercase',
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  Assign Driver
                </Button>
              )}

              {delivery.status === 'assigned' && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<InventoryIcon />}
                  onClick={() => handleStatusUpdate('picked_up')}
                  disabled={statusMutation.isPending}
                  sx={{
                    backgroundColor: palette.accent,
                    textTransform: 'uppercase',
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  Mark as Picked Up
                </Button>
              )}

              {delivery.status === 'picked_up' && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<DirectionsCarIcon />}
                  onClick={() => handleStatusUpdate('out_for_delivery')}
                  disabled={statusMutation.isPending}
                  sx={{
                    backgroundColor: '#9C27B0',
                    textTransform: 'uppercase',
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  Out for Delivery
                </Button>
              )}

              {delivery.status === 'out_for_delivery' && (
                <>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => handleStatusUpdate('delivered')}
                    disabled={statusMutation.isPending}
                    sx={{
                      backgroundColor: palette.success,
                      textTransform: 'uppercase',
                      fontWeight: typography.fontWeight.bold,
                    }}
                  >
                    Mark as Delivered
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<WarningIcon />}
                    onClick={() => handleStatusUpdate('failed')}
                    disabled={statusMutation.isPending}
                    sx={{
                      textTransform: 'uppercase',
                      fontWeight: typography.fontWeight.bold,
                    }}
                  >
                    Mark as Failed
                  </Button>
                </>
              )}

              {['pending', 'assigned', 'picked_up'].includes(delivery.status) && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={() => handleStatusUpdate('cancelled')}
                  disabled={statusMutation.isPending}
                  sx={{
                    textTransform: 'uppercase',
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  Cancel Delivery
                </Button>
              )}

              {delivery.status === 'failed' && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AssignmentIndIcon />}
                  onClick={() => handleStatusUpdate('assigned')}
                  disabled={statusMutation.isPending}
                  sx={{
                    backgroundColor: palette.info,
                    textTransform: 'uppercase',
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  Retry - Reassign
                </Button>
              )}
            </Box>
          </Card>

          {/* Timeline Card */}
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 3,
              ...animations.slideUp(0.2),
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                letterSpacing: typography.letterSpacing.widest,
                fontWeight: typography.fontWeight.bold,
              }}
            >
              DELIVERY TIMELINE
            </Typography>

            <Box sx={{ mt: 2 }}>
              {timelineEvents.map((event: any, index: number) => {
                const eventColor = getStatusColor(event.status);
                const isLast = index === timelineEvents.length - 1;

                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      position: 'relative',
                      pb: isLast ? 0 : 3,
                    }}
                  >
                    {/* Timeline line */}
                    {!isLast && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 15,
                          top: 32,
                          bottom: 0,
                          width: 2,
                          backgroundColor: colors.border,
                        }}
                      />
                    )}

                    {/* Timeline dot */}
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: alpha(eventColor, 0.1),
                        border: `2px solid ${eventColor}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        zIndex: 1,
                      }}
                    >
                      <Box sx={{ color: eventColor, display: 'flex', fontSize: 16 }}>
                        {getStatusIcon(event.status)}
                      </Box>
                    </Box>

                    {/* Content */}
                    <Box sx={{ ml: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: typography.fontFamily.display,
                          fontWeight: typography.fontWeight.semibold,
                          color: colors.textPrimary,
                        }}
                      >
                        {event.label}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: typography.fontFamily.mono,
                            color: colors.textMuted,
                          }}
                        >
                          {formatDateTime(event.time)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Estimated Delivery */}
            {delivery.estimatedDeliveryTime && (
              <Box
                sx={{
                  mt: 3,
                  pt: 2,
                  borderTop: `1px solid ${colors.border}`,
                }}
              >
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  ESTIMATED DELIVERY
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.bold,
                    color: palette.primary,
                  }}
                >
                  {formatDateTime(delivery.estimatedDeliveryTime)}
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Failed Dialog */}
      <Dialog open={failedDialogOpen} onClose={() => setFailedDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Delivery as Failed</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Failure Reason"
            fullWidth
            multiline
            rows={3}
            value={failureReason}
            onChange={(e) => setFailureReason(e.target.value)}
            placeholder="Please provide a reason for the failed delivery..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFailedDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleFailedConfirm}
            color="error"
            variant="contained"
            disabled={!failureReason.trim()}
          >
            Mark as Failed
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
